import type { NamedScopeTitle, ScopeText } from "@ext/articleProvider/logic/ArticleScopes";
import UiLanguage from "@ext/localization/core/model/Language";
import t, { getCurrentLanguage, type TranslationKey } from "@ext/localization/locale/translate";
import type { FragmentError } from "@ext/markdown/elements/fragment/edit/model/types";

// A replacer function keeps `$&` and the like in an id from being read as replacement patterns.
const fillIn = (text: string, values: Record<string, string>) =>
	Object.entries(values).reduce((filled, [name, value]) => filled.replace(`{{${name}}}`, () => value), text);

const fill = (key: TranslationKey, values: Record<string, string>) => fillIn(t(key), values);

const pick = (text: ScopeText): string | undefined =>
	text[getCurrentLanguage()] ?? text[UiLanguage.en] ?? Object.values(text)[0];

const pickTitle = ({ name, title }: NamedScopeTitle) => pick(title) ?? name;

const titles = (scopes: NamedScopeTitle[]) => scopes.map(pickTitle).join(", ");

const getFragmentErrorMessage = (error?: FragmentError): string => {
	switch (error?.reason) {
		case "not-found":
			return fill("fragment-error.not-found", { id: error.id, path: error.path });
		case "not-found-in-scopes":
			return error.path
				? fill("fragment-error.not-found-anywhere-at", {
						id: error.id,
						scope: titles(error.scopes),
						path: error.path,
					})
				: fill("fragment-error.not-found-anywhere", { id: error.id, scope: titles(error.scopes) });
		case "unknown-scope":
			return fill("fragment-error.unknown-scope", { id: error.id, scope: error.scope });
		case "unavailable":
			return error.scopes.length
				? fill("fragment-error.unavailable-in", { id: error.id, scope: titles(error.scopes) })
				: fill("fragment-error.unavailable", { id: error.id });
		case "scope-not-ready":
			return fillIn(pick(error.text) ?? t("cant-get-fragment-data"), { id: error.id });
		case "cycle":
			return fill("fragment-error.cycle", { id: error.id, path: error.path });
		case "failed":
			return fill("fragment-error.failed", { path: error.path });
		default:
			return t("cant-get-fragment-data");
	}
};

export default getFragmentErrorMessage;
